import { cn } from "@/lib/utils";
import { Deploy } from "@/types";
import { useMemo } from "react";

interface BuildLogsListProps {
  deploy: Deploy;
}

type DisplayLog = {
  id: string;
  level: string;
  message: string;
  stackTrace?: string;
  timestamp?: string;
};

// Cyberpunk Log Styles
const LOG_STYLES: Record<string, { color: string; label: string }> = {
  error: {
    label: "ERR",
    color: "text-[var(--error)]",
  },
  warn: {
    label: "WRN",
    color: "text-[var(--warning)]",
  },
  success: {
    label: "SUC",
    color: "text-[var(--success)]",
  },
  info: {
    label: "INF",
    color: "text-[var(--primary)]",
  },
};

const detailCardClass =
  "rounded border border-[var(--border-color)] bg-[var(--bg-alt)]/50 px-4 py-3";

const normalizeLevel = (level: string) => {
  const normalized = level.toLowerCase();
  return normalized === "warning" ? "warn" : normalized;
};

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "--:--:--";
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3, // Milliseconds for extra tech feel
  } as any);
};

export function BuildLogsList({ deploy }: BuildLogsListProps) {
  const logs = useMemo<DisplayLog[]>(() => {
    return (deploy.cicdLogs ?? []).map((log, index) => ({
      id: `build-${index}-${log.createdAt}`,
      timestamp: log.createdAt,
      level: (log.level ?? "info").toLowerCase(),
      message: log.message,
      stackTrace: log.stackTrace,
    }));
  }, [deploy.cicdLogs]);

  if (logs.length === 0) {
    return (
      <div
        className={cn(
          detailCardClass,
          "flex flex-col items-center justify-center py-12 text-[var(--text-subtle)] border-dashed border-[var(--border-color)] font-mono",
        )}
      >
        <p className="text-sm font-medium">&gt; NO_LOGS_DETECTED</p>
        <p className="text-xs opacity-70 mt-2">
          Waiting for pipeline initialization...
          <span className="animate-pulse">_</span>
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col font-mono text-xs space-y-0.5 pb-4">
      {logs.map((log) => {
        const normalized = normalizeLevel(log.level);
        const style = LOG_STYLES[normalized] ?? LOG_STYLES.info;

        return (
          <div
            key={log.id}
            className="group flex items-start gap-3 py-0.5 hover:bg-[var(--primary)]/5 transition-colors pl-2 border-l-2 border-transparent hover:border-[var(--primary)]"
          >
            {/* Timestamp Column - Dimmed */}
            <span className="shrink-0 w-[90px] text-[var(--text-subtle)] opacity-40 select-none">
              {formatTime(log.timestamp)}
            </span>

            {/* Level Column */}
            <span
              className={cn(
                "shrink-0 w-[24px] font-bold select-none",
                style.color,
              )}
            >
              {style.label}
            </span>

            <span className="shrink-0 text-[var(--text-subtle)] opacity-30">
              |
            </span>

            {/* Message Column */}
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  "break-words leading-tight text-[var(--text-main)]",
                  normalized === "error" && "text-[var(--error)]",
                  normalized === "warn" && "text-[var(--warning)]",
                )}
              >
                {log.message}
              </p>

              {/* Stack Trace - Code Block Style */}
              {log.stackTrace && (
                <div className="mt-1 pl-4 border-l border-[var(--error)]/50 opacity-90">
                  <pre className="overflow-x-auto p-2 text-[10px] leading-relaxed text-[var(--error)] bg-[var(--error)]/5 rounded-sm">
                    {log.stackTrace}
                  </pre>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Blinking Cursor at end of log stream */}
      <div className="pl-2 pt-1 animate-pulse text-[var(--primary)]">
        &gt;{" "}
        <span className="w-2 h-4 bg-[var(--primary)] inline-block align-middle ml-1"></span>
      </div>
    </div>
  );
}
